import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Key, Eye, EyeOff, Save, Trash2, CheckCircle, AlertTriangle, Loader2, Settings, Cpu, Bot, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PROVIDERS = [
  {
    id: 'gemini',
    name: 'Google Gemini',
    field: 'gemini_api_key',
    color: '#4285F4',
    models: ['gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'],
    docsUrl: 'https://aistudio.google.com/app/apikey',
    description: 'Google Gemini — ottimo per generazione testo veloce e multimodale.',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    field: 'openai_api_key',
    color: '#10A37F',
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'],
    docsUrl: 'https://platform.openai.com/api-keys',
    description: 'OpenAI GPT — massima versatilità e qualità di generazione.',
  },
  {
    id: 'anthropic',
    name: 'Anthropic Claude',
    field: 'anthropic_api_key',
    color: '#D4A574',
    models: ['claude-sonnet-4-5', 'claude-opus-4-5', 'claude-haiku-4-5'],
    docsUrl: 'https://console.anthropic.com/settings/keys',
    description: 'Claude — ideale per testi lunghi, ragionamento e precisione.',
  },
];

const AI_FUNCTIONS = [
  { id: 'ai_marketing', label: 'Sequenza Marketing AI', providerKey: 'ai_marketing_provider', modelKey: 'ai_marketing_model' },
  { id: 'ai_booking', label: 'AI Booking Assistant', providerKey: 'ai_booking_provider', modelKey: 'ai_booking_model' },
];

export function SettingsTab({ storedKey }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keys, setKeys] = useState({ gemini_api_key: '', openai_api_key: '', anthropic_api_key: '', custom_llm_base_url: '' });
  const [models, setModels] = useState({ ai_marketing_provider: 'emergent', ai_marketing_model: 'claude-sonnet-4-5', ai_booking_provider: 'emergent', ai_booking_model: 'claude-sonnet-4-5' });
  const [showKey, setShowKey] = useState({});
  const [deleting, setDeleting] = useState(null);

  const headers = { 'X-Superadmin-Key': storedKey };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/superadmin/settings/ai-keys`, { headers });
      setConfig(data);
      const m = data.models || {};
      setModels({
        ai_marketing_provider: m.ai_marketing_provider || 'emergent',
        ai_marketing_model: m.ai_marketing_model || 'claude-sonnet-4-5',
        ai_booking_provider: m.ai_booking_provider || 'emergent',
        ai_booking_model: m.ai_booking_model || 'claude-sonnet-4-5',
      });
    } catch { toast.error('Errore caricamento impostazioni AI'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/superadmin/settings/ai-keys`, { ...keys, ...models }, { headers });
      toast.success('Impostazioni AI salvate');
      load();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const deleteKey = async (provider) => {
    setDeleting(provider);
    try {
      await axios.delete(`${API}/api/superadmin/settings/ai-keys/${provider}`, { headers });
      toast.success(`Chiave ${provider} rimossa`);
      load();
    } catch { toast.error('Errore rimozione chiave'); }
    finally { setDeleting(null); }
  };

  const getModelsForProvider = (provider) => {
    const p = PROVIDERS.find(x => x.id === provider);
    return p ? p.models : [];
  };

  if (loading) return (
    <div className="flex items-center justify-center h-48 gap-2" style={{ color: 'var(--lux-text-muted)' }}>
      <Loader2 size={18} className="animate-spin" /> Caricamento impostazioni...
    </div>
  );

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-serif font-light" style={{ color: 'var(--lux-text)' }}>Impostazioni AI</h2>
          <p className="text-xs mt-1" style={{ color: 'var(--lux-text-muted)' }}>Configura i provider AI da usare per marketing e booking assistant.</p>
        </div>
        <button onClick={load} className="p-2 rounded" style={{ color: 'var(--lux-text-muted)' }} data-testid="settings-refresh-btn">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Emergent LLM Key Status */}
      <div className="rounded-sm p-4 flex items-center gap-3" style={{ background: config?.emergent_available ? '#0a1f0a' : '#1a0a0a', border: `1px solid ${config?.emergent_available ? '#1a4a1a' : '#4a1a1a'}` }}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs" style={{ background: config?.emergent_available ? '#166534' : '#7f1d1d' }}>
          {config?.emergent_available ? <CheckCircle size={14} style={{ color: '#4ade80' }} /> : <AlertTriangle size={14} style={{ color: '#f87171' }} />}
        </div>
        <div>
          <p className="text-xs font-semibold" style={{ color: config?.emergent_available ? '#4ade80' : '#f87171' }}>
            Emergent LLM Key — {config?.emergent_available ? 'Attiva (default)' : 'Non configurata'}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
            {config?.emergent_available
              ? 'Chiave universale Emergent disponibile come fallback per tutti i modelli Claude, Gemini, OpenAI.'
              : 'Nessuna Emergent LLM Key trovata. Configura almeno un provider.'}
          </p>
        </div>
      </div>

      {/* API Keys dei Provider */}
      <div className="space-y-4">
        <h3 className="text-xs uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>API Keys Provider</h3>
        {PROVIDERS.map(prov => {
          const provConfig = config?.providers?.[prov.id];
          const isConfigured = provConfig?.configured;
          return (
            <div key={prov.id} className="rounded-sm p-5" style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Cpu size={13} style={{ color: prov.color }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{prov.name}</span>
                    {isConfigured && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: '#052e16', color: '#4ade80', border: '1px solid #166534' }}>
                        Configurata
                      </span>
                    )}
                  </div>
                  <p className="text-[11px]" style={{ color: 'var(--lux-text-muted)' }}>{prov.description}</p>
                  {isConfigured && provConfig?.key_preview && (
                    <p className="text-[11px] mt-1 font-mono" style={{ color: 'var(--lux-text-muted-2)' }}>
                      Chiave attuale: {provConfig.key_preview}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a href={prov.docsUrl} target="_blank" rel="noreferrer"
                    className="p-1.5 rounded transition-colors"
                    style={{ color: 'var(--lux-text-muted)' }}
                    title="Ottieni API Key">
                    <ExternalLink size={13} />
                  </a>
                  {isConfigured && (
                    <button onClick={() => deleteKey(prov.id)} disabled={deleting === prov.id}
                      className="p-1.5 rounded transition-colors"
                      style={{ color: '#ef4444' }}
                      title="Rimuovi chiave"
                      data-testid={`delete-key-${prov.id}`}>
                      {deleting === prov.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    </button>
                  )}
                </div>
              </div>

              {/* Input nuova key */}
              <div className="relative">
                <Key size={12} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--lux-text-muted-2)' }} />
                <input
                  type={showKey[prov.id] ? 'text' : 'password'}
                  placeholder={isConfigured ? `Nuova chiave per sovrascrivere (${prov.name})` : `Incolla la tua ${prov.name} API Key`}
                  value={keys[prov.field]}
                  onChange={e => setKeys(p => ({ ...p, [prov.field]: e.target.value }))}
                  className="w-full pl-8 pr-10 py-2.5 text-xs rounded-sm font-mono"
                  style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
                  data-testid={`api-key-input-${prov.id}`}
                />
                <button onClick={() => setShowKey(p => ({ ...p, [prov.id]: !p[prov.id] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--lux-text-muted)' }}>
                  {showKey[prov.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom LLM Base URL */}
      <div className="rounded-sm p-5" style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Settings size={13} style={{ color: 'var(--lux-text-muted)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>Custom LLM Base URL</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)' }}>Avanzato</span>
        </div>
        <p className="text-[11px] mb-3" style={{ color: 'var(--lux-text-muted)' }}>Per servizi LiteLLM self-hosted o proxy personalizzati.</p>
        <input
          type="text"
          placeholder="https://your-litellm-proxy.example.com"
          value={keys.custom_llm_base_url}
          onChange={e => setKeys(p => ({ ...p, custom_llm_base_url: e.target.value }))}
          className="w-full px-3 py-2.5 text-xs rounded-sm font-mono"
          style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
          data-testid="custom-llm-url-input"
        />
      </div>

      {/* Modelli per funzione */}
      <div className="space-y-4">
        <h3 className="text-xs uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>Modello per Funzione</h3>
        {AI_FUNCTIONS.map(fn => (
          <div key={fn.id} className="rounded-sm p-5" style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Bot size={13} style={{ color: 'var(--lux-gold)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{fn.label}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--lux-text-muted)' }}>Provider</label>
                <select
                  value={models[fn.providerKey]}
                  onChange={e => {
                    const newProvider = e.target.value;
                    const defaultModels = { emergent: 'claude-sonnet-4-5', gemini: 'gemini-2.0-flash', openai: 'gpt-4o', anthropic: 'claude-sonnet-4-5' };
                    setModels(p => ({ ...p, [fn.providerKey]: newProvider, [fn.modelKey]: defaultModels[newProvider] || '' }));
                  }}
                  className="w-full px-3 py-2 text-xs rounded-sm"
                  style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
                  data-testid={`provider-select-${fn.id}`}>
                  <option value="emergent">Emergent (default)</option>
                  {PROVIDERS.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--lux-text-muted)' }}>Modello</label>
                {models[fn.providerKey] === 'emergent' ? (
                  <input
                    type="text"
                    value={models[fn.modelKey]}
                    onChange={e => setModels(p => ({ ...p, [fn.modelKey]: e.target.value }))}
                    className="w-full px-3 py-2 text-xs rounded-sm"
                    style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
                    data-testid={`model-input-${fn.id}`}
                  />
                ) : (
                  <select
                    value={models[fn.modelKey]}
                    onChange={e => setModels(p => ({ ...p, [fn.modelKey]: e.target.value }))}
                    className="w-full px-3 py-2 text-xs rounded-sm"
                    style={{ background: 'var(--lux-bg)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
                    data-testid={`model-select-${fn.id}`}>
                    {getModelsForProvider(models[fn.providerKey]).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Salva */}
      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-sm transition-opacity"
        style={{ background: 'var(--lux-gold)', color: '#000', opacity: saving ? 0.7 : 1 }}
        data-testid="save-ai-settings-btn">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? 'Salvataggio...' : 'Salva Impostazioni AI'}
      </button>

    </div>
  );
}
